import React, {useEffect, useState} from "react";
import {SettingsContainer, SettingsTabContainer} from "../../../../pages/user/settings/styled";
import Message from "../../../../pages/components/message";
import {MessageSections} from "../../../../pages/components/message/message";
import {Grid} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {
    getCities,
    getCountries,
    getProvinces,
    getRecipientDocumentTypes,
    getRegions
} from "../../../../redux/slices/static-data.slice";
import {useAppDispatch, useAppSelector} from "../../../../redux/store";
import {User} from "../../../../pages/shared/models/user/user";
import allUsersService from "../../../../services/all-users.service";
import {ActionBarWithSaveButton} from "../../../../pages/user/settings/user-actionbar/action-bar-with-save-button";
import {StandardDialog} from "../../../../pages/components/dialogs/standard-dialog/standard-dialog";
import {PersonalInfoChangePassword} from "../../../../pages/user/settings/personal-info/change-password/personal-info-change-password";
import {ToastContainer} from "react-toastify";
import BarekamText from "../../../../pages/components/text";
import {handleUserSave, initSuccessToast} from "../../../../pages/user/settings/personal-info/personal-info.service";
import {SaveResultType} from "../../../../pages/shared/enum/save-result-type";
import {getCurrentUserData} from "../../../../redux/slices/current-user.slice";
import Joi from "joi";
import {WarehouseWorkerAccount} from "./warehouse-blocks/warehouse-worker-account";
import {GapContainer} from "../../../../pages/components/card/styled";
import BarekamButton, {SimpleButtons} from "../../../../pages/components/button";
import {BarekamIcon, IconVariants} from "../../../../pages/components/icon/barekam-icon";
import {WarehouseModel} from "../../../../pages/shared/models/order/warehouse.model";
import {CountryModel} from "../../../../pages/shared/models/countryModel";
import {getMessage} from "../../../../pages/shared/helper-functions";

const HeaderText = ({firstName, lastName}: {firstName: string, lastName: string}) => {
    const navigate = useNavigate();

    return (
        <GapContainer style={{minHeight: '6rem'}}>
            <BarekamButton type={SimpleButtons.LINK} onClick={() => navigate(-1)}>
                <BarekamIcon variant={IconVariants.back}/>
            </BarekamButton>

            <BarekamText variant={'xxl'} bold>
                {firstName} {lastName}
            </BarekamText>
        </GapContainer>
    )
}

export const WarehouseWorkersView = () => {
    const [isReady, setIsReady] = useState(false);
    const [isModified, setIsModified] = useState(false);

    const [warehouses, setWarehouses] = useState<WarehouseModel[]>([]);
    const {countries} = useAppSelector(state => state.staticDataReducer);

    const [warehouseWorker, setWarehouseWorker] = useState<User>();
    const [modifiedWarehouseWorker, setModifiedWarehouseWorker] = useState<User>();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [changePasswordDialogOpened, setChangePasswordDialogOpened] = useState(false);

    const {warehouseWorkerId} = useParams();

    const {messageCache, languageId} = useAppSelector(state => state.messagesReducer);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (warehouseWorkerId && +warehouseWorkerId) {
            Promise.allSettled([
                allUsersService.getUserById(+warehouseWorkerId),
                dispatch(getCountries()),
                dispatch(getProvinces()),
                dispatch(getCities()),
                dispatch(getRegions()),
                dispatch(getRecipientDocumentTypes()),
            ]).then(([customerResponse]) => {
                if (customerResponse.status === 'fulfilled') {
                    const customer = customerResponse.value?.data;
                    setWarehouseWorker(customer);
                    setModifiedWarehouseWorker(customer);
                }
            })
        }
    }, [])

    useEffect(() => {
        setWarehouses(() => {
            return countries
                .map(country => country.warehouse)
                .flat();
        })
    }, [countries])

    useEffect(() => {
        if (warehouses && warehouseWorker) {
            setIsReady(true);
        }
    }, [warehouses, warehouseWorker])


    function handleUserFieldValueChange(value: any, field: string) {
        setIsModified(true);

        setModifiedWarehouseWorker(prevState => {
            if (prevState) {
                switch (field) {
                    case 'warehouse':
                        const warehouse = warehouses.find(warehouse => warehouse.warehouseId === value);
                        return {...prevState, [field]: warehouse};
                    case 'locationId':
                        const country = countries.find(country => country.countryId === value);
                        return {...prevState, [field]: country as CountryModel};
                    default:
                        return {...prevState, [field]: value};
                }
            }
        })
    }

    function handleSaveAction() {
        const successMessage = getMessage('saved', MessageSections.USER, messageCache, languageId);

        if (warehouseWorker && modifiedWarehouseWorker) {
            handleUserSave(modifiedWarehouseWorker, warehouseWorker)
                .then(result => {
                    if (result.status === SaveResultType.SUCCESS) {
                        dispatch(getCurrentUserData()).then(() => {
                            initSuccessToast(successMessage);
                            setIsModified(false);
                            setErrors({});
                        })
                    } else if (Array.isArray(result)) {
                        setErrors(result.reduce((result: { [key: string]: string }, error: Joi.ValidationErrorItem) => {
                            result[error.path[0] as string] = error.message;
                            return result;
                        }, {}) || {});
                    }
                })
        }
    }

    const PrimaryActionText = () => (
        <BarekamText variant={"m"} bold>
            <Message messageKey={'save'} sectionId={MessageSections.USER}/>
        </BarekamText>
    )

    if (isReady) {
        return (
            <SettingsContainer>
                <ActionBarWithSaveButton disabled={!!warehouseWorker}
                                         headerText={<HeaderText firstName={warehouseWorker?.firstName || ''}
                                                       lastName={warehouseWorker?.lastName || ''} />}
                                         primaryActionText={isModified ? <PrimaryActionText/> : undefined}
                                         onPrimaryActionClick={handleSaveAction}/>

                <SettingsTabContainer>
                    <Grid container columnSpacing={'1.5rem'} rowSpacing={'1.5rem'} width={'50%'}>
                        <WarehouseWorkerAccount userData={modifiedWarehouseWorker}
                                                onChange={handleUserFieldValueChange}
                                                errors={errors}
                                                onPasswordChange={() => setChangePasswordDialogOpened(true)}
                                                warehouses={warehouses}
                                                countries={countries}/>
                    </Grid>

                    <StandardDialog headerLabel={<Message messageKey={'change_password'} sectionId={MessageSections.USER}/>}
                                    open={changePasswordDialogOpened}>
                        <PersonalInfoChangePassword onClose={() => setChangePasswordDialogOpened(false)}/>
                    </StandardDialog>

                    <ToastContainer/>
                </SettingsTabContainer>
            </SettingsContainer>
        );
    } else {
        return <></>
    }
}